/**
 * SRES · KML CRS Correction Preview (Iter98k · Round 5 · Feb 2026).
 *
 * Modal workflow that lets the user upload a KML/KMZ (or CSV), inspect
 * the detected CRS, choose a correction path, preview reprojected
 * coordinates and approve the save. Every step is fully backed by
 * existing /crs/* endpoints — this component is pure UI.
 *
 * Correction paths surfaced to the user:
 *   1. Keep original coordinates (no reprojection)
 *   2. Convert Arc 1960 → WGS 84 (auto-detect UTM zone)
 *   3. Manual Arc 1960 UTM zone (35/36/37 S)
 *   4. Manual source CRS (any EPSG from the registry)
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import {
  UploadSimple, ArrowsClockwise, CheckCircle,
  Warning, MapPin, FloppyDisk, ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  validateUploadFile, previewTransformation, saveProjectCRS,
  detectArc1960, previewArc1960, approveArc1960,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";


const CORRECTION_PATHS = [
  { key: "keep",       label: "Keep original coordinates" },
  { key: "arc_auto",   label: "Auto-convert Arc 1960 → WGS 84" },
  { key: "arc_manual", label: "Manual Arc 1960 UTM zone" },
  { key: "custom",     label: "Manual source EPSG" },
];

const ARC_UTM_ZONES = [
  { epsg: "21035", label: "Arc 1960 / UTM zone 35 S" },
  { epsg: "21036", label: "Arc 1960 / UTM zone 36 S" },
  { epsg: "21037", label: "Arc 1960 / UTM zone 37 S" },
];


export default function CRSCorrectionPreviewModal({ pid, open, onClose,
                                                       onApproved }) {
  const { user } = useAuth();
  const [file, setFile]                       = useState(null);
  const [uploading, setUploading]             = useState(false);
  const [validation, setValidation]           = useState(null);
  const [path, setPath]                       = useState("keep");
  const [manualZone, setManualZone]           = useState("21036");
  const [customEpsg, setCustomEpsg]           = useState("");
  const [preview, setPreview]                 = useState(null);
  const [previewing, setPreviewing]           = useState(false);
  const [saving, setSaving]                   = useState(false);
  // Iter108 · Arc 1960 auto-detect verdict + preview for
  // Kenyan / Tanzanian / Ugandan projects. Populated automatically
  // after the upload validation returns.
  const [arc1960, setArc1960]                 = useState(null);
  const [arc1960Loading, setArc1960Loading]   = useState(false);
  const [arc1960Approving, setArc1960Approving] = useState(false);

  const reset = useCallback(() => {
    setFile(null); setValidation(null); setPath("keep");
    setManualZone("21036"); setCustomEpsg("");
    setPreview(null); setArc1960(null);
  }, []);

  const handleUpload = async (f) => {
    if (!f) return;
    setFile(f); setUploading(true); setValidation(null); setPreview(null);
    setArc1960(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await validateUploadFile(pid, fd);
      setValidation(r);
      // Pick a sensible default correction path
      const ct = r?.coordinate_type?.type;
      if (ct === "geographic")                     setPath("keep");
      else if (ct === "projected_utm_arc_1960")    setPath("arc_auto");
      else                                          setPath("custom");
      // Iter108 · Auto-run Arc 1960 heuristic for East-African projects.
      // Uses the same sample coordinates the validator returned so we
      // don't re-upload the file. Non-blocking — errors are silent.
      const xs = r?.sample_x || [];
      const ys = r?.sample_y || [];
      if (xs.length && ys.length) {
        setArc1960Loading(true);
        try {
          const rows = xs.slice(0, 20).map((x, i) => ({
            x, y: ys[i], hole_id: `sample-${i + 1}`,
          }));
          const arc = await previewArc1960({
            rows,
            declared_epsg: r?.detected_crs?.epsg
              ? `EPSG:${r.detected_crs.epsg}` : undefined,
            country: r?.project_country || undefined,
            n: 5,
          });
          setArc1960(arc);
          // Auto-select arc_auto when we get a strong signal.
          if (arc?.detection?.arc1960_likely) setPath("arc_auto");
        } catch {
          /* silent — Arc 1960 is optional */
        } finally { setArc1960Loading(false); }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Upload failed");
    } finally { setUploading(false); }
  };

  // Iter108 · Approve the Arc 1960 → WGS 84 correction directly, skipping
  // the manual preview / approve loop for the common East-African case.
  const handleApproveArc1960 = async () => {
    const det = arc1960?.detection;
    if (!det?.arc1960_likely) return;
    setArc1960Approving(true);
    try {
      const shift = arc1960?.preview?.samples?.[0]?.shift_m ?? null;
      // Persist to the CRS audit trail (transform_type = arc1960_to_wgs84).
      await approveArc1960(pid, {
        source_epsg:  det.suggested_source,
        target_epsg:  det.target_wgs84,
        approved_by:  user?.email || user?.id || "unknown",
        sample_shift_m: shift,
        filename:     file?.name || "kml_upload",
      });
      // ALSO record via saveProjectCRS so the standard CRS store reflects
      // the same decision (keeps existing viewer & audit-log rows in sync).
      await saveProjectCRS(pid, {
        source_epsg: det.suggested_source,
        target_epsg: det.target_wgs84,
        approved_by: user?.email || user?.id || "unknown",
        approval_reason:
          `Arc 1960 → WGS 84 auto-correction · confidence ${Math.round(
            (det.confidence || 0) * 100)}% · signals: `
          + (det.signals || []).join(", "),
        source_file: file?.name || "kml_upload",
      });
      toast.success(
        `Arc 1960 correction approved (${det.suggested_source} → ${det.target_wgs84}, ~${Math.round(
          shift || det.typical_offset_m)} m shift).`
      );
      onApproved?.({
        src: det.suggested_source, tgt: det.target_wgs84, path: "arc_auto",
      });
      reset(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message
                     || "Arc 1960 approval failed");
    } finally { setArc1960Approving(false); }
  };

  const targetEpsg = () => {
    if (path === "keep")       return validation?.detected_crs?.epsg;
    if (path === "arc_auto")   return "4326";
    if (path === "arc_manual") return "4326";
    if (path === "custom")     return customEpsg;
    return null;
  };

  const sourceEpsg = () => {
    if (path === "keep")       return validation?.detected_crs?.epsg;
    if (path === "arc_auto") {
      // Prefer the recommendation from the backend if it detected Arc UTM.
      return (validation?.candidate_epsg_utm_south?.[0]?.toString()
              || "21036");
    }
    if (path === "arc_manual") return manualZone;
    if (path === "custom")     return customEpsg;
    return null;
  };

  const handlePreview = async () => {
    if (!validation) return;
    const src = sourceEpsg(); const tgt = targetEpsg();
    if (!src || !tgt) {
      toast.error("Source / target EPSG missing"); return;
    }
    setPreviewing(true);
    try {
      const rows = (validation.sample_x || []).slice(0, 5).map((x, i) => ({
        x, y: (validation.sample_y || [])[i],
      }));
      const r = await previewTransformation(pid, {
        rows, source_epsg: `EPSG:${src}`, target_epsg: `EPSG:${tgt}`,
        x_col: "x", y_col: "y", n: 5,
      });
      setPreview({ ...r, source_epsg: src, target_epsg: tgt });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const handleApprove = async () => {
    const src = sourceEpsg(); const tgt = targetEpsg();
    if (!src || !tgt) { toast.error("Cannot save — EPSG missing"); return; }
    setSaving(true);
    try {
      await saveProjectCRS(pid, {
        source_epsg:  `EPSG:${src}`,
        target_epsg:  `EPSG:${tgt}`,
        approved_by:  user?.email || user?.id || "unknown",
        approval_reason: (path === "keep")
          ? "Coordinates kept as detected (no reprojection required)."
          : `KML CRS correction · ${path} · source EPSG:${src} → target EPSG:${tgt}`,
        source_file:  file?.name || "kml_upload",
      });
      toast.success("CRS correction approved — original file preserved in audit trail");
      onApproved?.({ src, tgt, path });
      reset(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
                     data-testid="crs-correction-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-blue">
            <MapPin size={18} /> KML CRS Correction Preview
          </DialogTitle>
        </DialogHeader>

        {/* Step 1 — upload */}
        <div className="rounded-md border border-slate-200 p-3">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
            Step 1 · Upload KML / KMZ / CSV
          </div>
          <label className="flex items-center gap-3 cursor-pointer text-[13px]">
            <input type="file" accept=".kml,.kmz,.csv" className="hidden"
                   data-testid="crs-preview-file-input"
                   onChange={(e) => handleUpload(e.target.files?.[0])} />
            <span className="inline-flex items-center gap-1.5 rounded border border-brand-blue bg-white px-3 py-1.5 text-brand-blue font-semibold hover:bg-brand-blue hover:text-white transition-colors">
              <UploadSimple size={13} /> Choose file
            </span>
            <span className="text-slate-600 truncate flex-1"
                     data-testid="crs-preview-filename">
              {file ? file.name : "no file selected"}
            </span>
            {uploading && (
              <ArrowsClockwise size={14} className="animate-spin text-brand-blue" />
            )}
          </label>
        </div>

        {/* Step 2 — detection summary */}
        {validation && (
          <div className="mt-4 rounded-md border border-slate-200 p-3"
               data-testid="crs-preview-detection">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
              Step 2 · Detected CRS
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-[12.5px]">
              <div><span className="text-slate-500">Detected columns:</span> <span className="font-mono">{validation.detected_columns?.x} / {validation.detected_columns?.y}</span></div>
              <div><span className="text-slate-500">Rows read:</span> <span className="font-mono">{validation.n_rows}</span></div>
              <div><span className="text-slate-500">Coordinate type:</span> <span className="font-mono">{validation.coordinate_type?.type}</span></div>
              <div><span className="text-slate-500">Confidence:</span> <span className="font-mono">{Math.round((validation.coordinate_type?.confidence || 0) * 100)}%</span></div>
              <div><span className="text-slate-500">Range X:</span> <span className="font-mono">{(validation.range_x || []).length ? (validation.range_x || []).map((n) => n?.toFixed?.(3) ?? n).join(" · ") : "n/a"}</span></div>
              <div><span className="text-slate-500">Range Y:</span> <span className="font-mono">{(validation.range_y || []).length ? (validation.range_y || []).map((n) => n?.toFixed?.(3) ?? n).join(" · ") : "n/a"}</span></div>
              {validation.swap_xy?.swapped && (
                <div className="col-span-2 flex items-center gap-1 text-amber-700">
                  <Warning size={12} /> XY appears swapped in this file — recommendation applied.
                </div>
              )}
              {validation.candidate_epsg_utm_south?.length ? (
                <div className="col-span-2 text-slate-600">
                  Candidate Arc 1960 UTM zones: <span className="font-mono">{validation.candidate_epsg_utm_south.join(", ")}</span>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Iter108 · Arc 1960 auto-detect card
            Shows only when we have a heuristic result. If arc1960_likely
            is true, the user can approve the datum shift with one click
            (source EPSG:21036/37/… → WGS 84). Falls back to the manual
            flow below when confidence is low. */}
        {arc1960?.detection && (
          <div className={`mt-4 rounded-md border p-3
                              ${arc1960.detection.arc1960_likely
                                  ? "border-amber-300 bg-amber-50"
                                  : "border-slate-200 bg-slate-50"}`}
               data-testid="crs-preview-arc1960">
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0
                                  ${arc1960.detection.arc1960_likely
                                      ? "bg-amber-500 text-white"
                                      : "bg-slate-300 text-slate-700"}`}>
                <MapPin size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate-600">
                    Arc 1960 auto-detect
                  </span>
                  {arc1960Loading && (
                    <ArrowsClockwise size={11}
                                        className="animate-spin text-slate-500" />
                  )}
                  <span className="ml-auto text-[10.5px] font-mono text-slate-500"
                            data-testid="crs-preview-arc1960-confidence">
                    confidence {Math.round((arc1960.detection.confidence || 0) * 100)}%
                  </span>
                </div>
                <p className="text-[12.5px] text-slate-700 mt-1">
                  {arc1960.detection.message}
                </p>
                {arc1960.detection.arc1960_likely && (
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11.5px]">
                    <div>
                      <span className="text-slate-500">Source (Arc 1960):</span>
                      &nbsp;<span className="font-mono text-brand-blue"
                                       data-testid="crs-preview-arc1960-source">
                        {arc1960.detection.suggested_source}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Target (WGS 84):</span>
                      &nbsp;<span className="font-mono text-brand-blue"
                                       data-testid="crs-preview-arc1960-target">
                        {arc1960.detection.target_wgs84}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">Typical offset:</span>
                      &nbsp;<span className="font-mono">
                        ~{Math.round(arc1960.detection.typical_offset_m)} m
                      </span>
                      {arc1960?.preview?.samples?.[0]?.shift_m != null && (
                        <>
                          &nbsp;·&nbsp;<span className="text-slate-500">
                            first-sample computed:
                          </span>
                          &nbsp;<span className="font-mono"
                                          data-testid="crs-preview-arc1960-shift">
                            {arc1960.preview.samples[0].shift_m.toFixed(1)} m
                          </span>
                        </>
                      )}
                    </div>
                    {(arc1960.detection.signals || []).length > 0 && (
                      <div className="col-span-2 text-[10.5px] text-slate-500">
                        Signals: {(arc1960.detection.signals || []).map((s) => (
                          <span key={s}
                                    className="inline-block bg-white border border-slate-200 rounded px-1.5 py-0.5 ml-1 font-mono">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {arc1960.detection.arc1960_likely && (
                  <div className="mt-3 flex items-center gap-2">
                    <Button size="sm"
                                disabled={arc1960Approving}
                                onClick={handleApproveArc1960}
                                className="bg-amber-600 hover:bg-amber-700 text-white text-[11.5px]"
                                data-testid="crs-preview-arc1960-approve">
                      <CheckCircle size={12} className="mr-1" />
                      {arc1960Approving ? "Applying…" : "Apply Arc 1960 → WGS 84"}
                    </Button>
                    <span className="text-[10.5px] text-slate-500">
                      Recorded in the CRS Audit Trail; original file preserved.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — correction choice */}
        {validation && (
          <div className="mt-4 rounded-md border border-slate-200 p-3"
               data-testid="crs-preview-correction-choice">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
              Step 3 · Choose correction path
            </div>
            <div className="grid grid-cols-2 gap-2">
              {CORRECTION_PATHS.map((p) => (
                <label key={p.key} className={`rounded border px-3 py-2 flex items-start gap-2 cursor-pointer transition-colors ${path === p.key ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="crspath" value={p.key}
                         checked={path === p.key}
                         onChange={() => { setPath(p.key); setPreview(null); }}
                         data-testid={`crs-preview-path-${p.key}`}
                         className="mt-0.5" />
                  <span className="text-[12.5px]">{p.label}</span>
                </label>
              ))}
            </div>
            {path === "arc_manual" && (
              <div className="mt-2 flex items-center gap-2 text-[12px]">
                <span className="text-slate-500">UTM zone:</span>
                <select value={manualZone}
                        onChange={(e) => { setManualZone(e.target.value); setPreview(null); }}
                        className="border border-slate-200 rounded px-2 py-1 text-[12px]"
                        data-testid="crs-preview-manual-zone">
                  {ARC_UTM_ZONES.map((z) => (
                    <option key={z.epsg} value={z.epsg}>{z.label}</option>
                  ))}
                </select>
              </div>
            )}
            {path === "custom" && (
              <div className="mt-2 flex items-center gap-2 text-[12px]">
                <span className="text-slate-500">Source EPSG code:</span>
                <input type="text" value={customEpsg}
                       onChange={(e) => { setCustomEpsg(e.target.value.replace(/[^0-9]/g, "")); setPreview(null); }}
                       placeholder="e.g. 32736"
                       className="border border-slate-200 rounded px-2 py-1 text-[12px] w-32 font-mono"
                       data-testid="crs-preview-custom-epsg" />
              </div>
            )}
          </div>
        )}

        {/* Step 4 — preview */}
        {validation && (
          <div className="mt-4 rounded-md border border-slate-200 p-3"
               data-testid="crs-preview-transform">
            <div className="flex items-center gap-3 mb-2">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 flex-1">
                Step 4 · Preview corrected coordinates
              </div>
              <Button size="sm" onClick={handlePreview}
                      disabled={previewing || path === "custom" && !customEpsg}
                      className="bg-slate-100 border border-slate-300 text-slate-700 hover:bg-slate-200 text-[11.5px]"
                      data-testid="crs-preview-run-btn">
                <ArrowClockwise size={12} className="mr-1" />
                {previewing ? "Previewing…" : "Preview"}
              </Button>
            </div>
            {preview && (
              <div className="table-wrapper">
                <table className="w-full text-[11px] font-mono"
                       data-testid="crs-preview-result-table">
                  <thead className="border-b border-slate-200 text-slate-500">
                    <tr>
                      <th className="text-left py-1">#</th>
                      <th className="text-right py-1">Original X</th>
                      <th className="text-right py-1">Original Y</th>
                      <th className="text-right py-1">Corrected X</th>
                      <th className="text-right py-1">Corrected Y</th>
                      <th className="text-left py-1 pl-2">Hole / Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.samples || preview.rows || []).map((s, i) => {
                      const xIn  = s.before?.x ?? s.x_in;
                      const yIn  = s.before?.y ?? s.y_in;
                      const xOut = s.after?.x  ?? s.x_out;
                      const yOut = s.after?.y  ?? s.y_out;
                      const note = s.hole_id  ?? s.note ?? "";
                      return (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1 text-slate-500">{i + 1}</td>
                          <td className="py-1 text-right">{xIn?.toFixed?.(3) ?? xIn}</td>
                          <td className="py-1 text-right">{yIn?.toFixed?.(3) ?? yIn}</td>
                          <td className="py-1 text-right text-brand-blue">{xOut?.toFixed?.(6) ?? xOut}</td>
                          <td className="py-1 text-right text-brand-blue">{yOut?.toFixed?.(6) ?? yOut}</td>
                          <td className="py-1 pl-2 text-slate-500">{note}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!preview && (
              <div className="text-[12px] text-slate-500 italic">
                Click Preview to see corrected coordinates for the first 5 rows.
              </div>
            )}
          </div>
        )}

        {/* Step 5 — approve */}
        {preview && (
          <div className="mt-4 rounded-md border-2 border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2"
               data-testid="crs-preview-approve">
            <CheckCircle size={16} className="text-emerald-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-[12.5px] text-slate-700">
              <b>Ready to apply.</b> The original file is preserved. An
              audit-log entry with old CRS, new CRS, transformation
              parameters, timestamp and current user will be recorded.
            </div>
            <Button size="sm" onClick={handleApprove} disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="crs-preview-approve-btn">
              <FloppyDisk size={12} className="mr-1" />
              {saving ? "Saving…" : "Approve & Save"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
